import React, { useState, useEffect } from 'react';
import { Doughnut, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { statsAPI } from '../services/api';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Select } from './ui/select';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, PieChart, BarChart3, Building2, Route, Car, TrendingUp, Bus, Hash } from 'lucide-react';
import { cn } from '../lib/utils';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const Statistics = () => {
  const [displayMode, setDisplayMode] = useState('bus_company'); // bus_company, route, bus_type
  const [chartType, setChartType] = useState('doughnut'); // doughnut, bar
  const [stats, setStats] = useState([]);
  const [overviewStats, setOverviewStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadStatistics();
  }, []);

  useEffect(() => {
    loadStatsByMode();
  }, [displayMode]);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      const overviewResponse = await statsAPI.getOverview();
      setOverviewStats(overviewResponse.data);
    } catch (error) {
      setError('載入統計數據失敗: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const loadStatsByMode = async () => {
    try {
      setLoading(true);
      let response;
      
      switch (displayMode) {
        case 'bus_company':
          response = await statsAPI.getBusCompanies();
          break;
        case 'route':
          response = await statsAPI.getRoutes();
          break;
        case 'bus_type':
          response = await statsAPI.getBusTypes();
          break;
        default:
          response = await statsAPI.getBusCompanies();
      }
      
      setStats(response.data || []);
    } catch (error) {
      setError('載入統計數據失敗: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const getDisplayModeConfig = () => {
    switch (displayMode) {
      case 'bus_company':
        return {
          title: '巴士公司使用統計',
          icon: Building2,
          labelKey: 'bus_company',
          emptyMessage: '暫無巴士公司數據'
        };
      case 'route':
        return {
          title: '巴士路線使用統計',
          icon: Route,
          labelKey: 'route',
          emptyMessage: '暫無路線數據'
        };
      case 'bus_type':
        return {
          title: '巴士車型使用統計',
          icon: Car,
          labelKey: 'bus_type',
          emptyMessage: '暫無車型數據'
        };
      default:
        return {
          title: '統計數據',
          icon: PieChart,
          labelKey: 'name',
          emptyMessage: '暫無數據'
        };
    }
  };

  const getChartData = () => {
    const config = getDisplayModeConfig();
    const colors = [
      '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
      '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#FF6384',
      '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'
    ];

    return {
      labels: stats.map(stat => stat[config.labelKey] || stat.name || '未知'),
      datasets: [
        {
          data: stats.map(stat => stat.count),
          backgroundColor: colors.slice(0, stats.length),
          borderColor: colors.slice(0, stats.length).map(color => color),
          borderWidth: 2,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          usePointStyle: true,
          font: {
            size: 12
          }
        },
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value} 次 (${percentage}%)`;
          }
        }
      }
    },
  };

  const config = getDisplayModeConfig();
  const ModeIcon = config.icon;

  if (loading && !stats.length) {
    return (
      <div
        className="container mx-auto px-2 sm:px-4 py-2 sm:py-4 md:py-6 max-w-6xl flex items-stretch"
        style={{ minHeight: 'calc(100vh - 80px)', paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        <Card className="shadow-lg w-full flex flex-col">
          <CardContent className="flex flex-col items-center justify-center min-h-[400px]">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">載入統計數據中...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="container mx-auto px-2 sm:px-4 py-2 sm:py-4 md:py-6 max-w-6xl flex items-stretch"
      style={{ minHeight: 'calc(100vh - 80px)', paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
    >
      <Card className="shadow-lg w-full flex flex-col">
        <CardContent>
      <div className="mb-6">
        <h2 className="text-3xl font-bold flex items-center gap-2 mb-2">
          <PieChart className="h-8 w-8" />
          巴士統計
        </h2>
        <p className="text-muted-foreground">查看您的巴士記錄統計數據</p>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">總記錄數</p>
                <p className="text-2xl font-bold">{overviewStats.total_records || 0}</p>
              </div>
              <Bus className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">巴士公司</p>
                <p className="text-2xl font-bold">{overviewStats.unique_companies || 0}</p>
              </div>
              <Building2 className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">不同路線</p>
                <p className="text-2xl font-bold">{overviewStats.unique_routes || 0}</p>
              </div>
              <Route className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">車型種類</p>
                <p className="text-2xl font-bold">{overviewStats.unique_bus_types || 0}</p>
              </div>
              <Car className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Display Mode Selector */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ModeIcon className="h-5 w-5" />
            {config.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="space-y-2">
              <Label htmlFor="display-mode">顯示方式</Label>
              <Select
                id="display-mode"
                value={displayMode}
                onChange={(e) => setDisplayMode(e.target.value)}
              >
                <option value="bus_company">巴士公司</option>
                <option value="route">巴士路線</option>
                <option value="bus_type">巴士車型</option>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="chart-type">圖表類型</Label>
              <Select
                id="chart-type"
                value={chartType}
                onChange={(e) => setChartType(e.target.value)}
              >
                <option value="doughnut">圓形圖 (Doughnut)</option>
                <option value="bar">長條圖 (Bar)</option>
              </Select>
            </div>
          </div>

          {/* Chart */}
          {stats.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className={cn("lg:col-span-2")}>
                <div style={{ height: '400px' }}>
                  {chartType === 'doughnut' ? (
                    <Doughnut data={getChartData()} options={chartOptions} />
                  ) : (
                    <Bar 
                      data={getChartData()} 
                      options={{
                        ...chartOptions,
                        scales: {
                          y: {
                            beginAtZero: true,
                            ticks: {
                              stepSize: 1
                            }
                          }
                        }
                      }} 
                    />
                  )}
                </div>
              </div>

              <div className="lg:col-span-1">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">詳細數據</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {stats.map((stat, index) => {
                        const total = stats.reduce((sum, s) => sum + s.count, 0);
                        const percentage = ((stat.count / total) * 100).toFixed(1);
                        const label = stat[config.labelKey] || stat.name || '未知';
                        
                        return (
                          <div key={index} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                            <div className="flex-1">
                              <p className="font-medium">{label}</p>
                              <p className="text-sm text-muted-foreground">{stat.count} 次</p>
                            </div>
                            <div className="text-right">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary text-primary-foreground">
                                {percentage}%
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <ModeIcon className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
              <h5 className="text-lg font-medium text-muted-foreground mb-2">{config.emptyMessage}</h5>
              <p className="text-sm text-muted-foreground">開始記錄巴士行程後即可查看統計</p>
            </div>
          )}
        </CardContent>
      </Card>

        </CardContent>
      </Card>
    </div>
  );
};

export default Statistics;
